import type { FailedTorsionRow, FittedTorsionRow } from "@/api/affdo";

/**
 * Which torsion a run surface is currently showing — the INNER axis of the
 * workspace's nested selection (VIZ U5.1).
 *
 * **The two axes are nested, not independent.** A `torsion_id` is only
 * meaningful inside its fragment: the per-fragment routes are keyed by
 * fragment and the profiles are joined `by torsion_id` within one fragment's
 * response, so the same id in another fragment is a different torsion — or
 * more usually, is simply not there. That is why a pick carries the fragment it
 * was made in (`TorsionPick`) and is resolved against that fragment's CURRENT
 * torsion list, rather than carrying a bare id or an index across a
 * fragment change: an index would silently re-point at whatever torsion
 * happens to sit at that position in the new fragment, which is the "looks
 * right, is not" class of defect. Same reasoning, one axis in, as
 * `fragment-selection.ts` — and reconciling against the live list is also what
 * makes a mid-run refresh safe, since a fragment's torsion list GROWS as fits
 * land.
 */

/**
 * The grid's display order: fitted torsions **worst-RMSD-first**, then the
 * failed ones.
 *
 * Lives here rather than inside `DihedralRMSDGrid` because the slider steps
 * through the same sequence the grid lays out, and a slider ordered
 * differently from the overview it zooms into would make "the next torsion"
 * mean two things. One ordering law, two readers.
 */
export function sortedFittedRows(
  fitted: readonly FittedTorsionRow[] | undefined
): FittedTorsionRow[] {
  if (!fitted) return [];
  return [...fitted].sort((a, b) => b.fit_rmsd_kcal_per_mol - a.fit_rmsd_kcal_per_mol);
}

/** Every torsion the fragment can show, in the order the grid draws them. */
export function orderedTorsionIds(
  fitted: readonly FittedTorsionRow[] | undefined,
  failed: readonly FailedTorsionRow[] | undefined
): string[] {
  return [
    ...sortedFittedRows(fitted).map((row) => row.torsion_id),
    ...(failed ?? []).map((row) => row.torsion_id),
  ];
}

/**
 * A pick, QUALIFIED BY THE FRAGMENT IT WAS MADE IN.
 *
 * A bare `torsion_id` is not a selection, because it is not unique across the
 * run: the server's own docstring scopes uniqueness to one fragment's result
 * (*"`torsion_id` is unique within a fragment's result"*,
 * `src/flowstack/server/routes/affdo.py:760`) and the per-fragment listing
 * sorts by the PAIR `(fragment_id, torsion_id)` (`:898`). So two fragments may
 * each carry a `t-1`, and they are different torsions. Storing the fragment
 * with the pick is what lets `resolveTorsionId` tell "the torsion the user
 * chose" from "a different torsion that happens to share its name"
 * (Rupert PR-1077 r1).
 */
export interface TorsionPick {
  fragmentId: string;
  torsionId: string;
}

/**
 * The torsion actually on screen, given what the user last picked.
 *
 * `picked` survives only while it was made **in this fragment** and still names
 * a torsion this fragment has; otherwise the first of the current list wins —
 * which, given the ordering above, is the worst-RMSD torsion, the one worth
 * looking at first. `null` only when the fragment has no torsions at all.
 *
 * Note the two guards are not redundant. The id check alone would carry a
 * same-named torsion into another fragment (the case above); the fragment check
 * alone would keep an id a mid-run refresh has since removed. The pick is also
 * kept rather than cleared on a fragment change, so stepping away from a
 * fragment and back returns to the torsion the user was reading.
 */
export function resolveTorsionId(
  torsionIds: readonly string[],
  picked: TorsionPick | null,
  fragmentId: string | null
): string | null {
  if (
    picked !== null &&
    picked.fragmentId === fragmentId &&
    torsionIds.includes(picked.torsionId)
  ) {
    return picked.torsionId;
  }
  return torsionIds[0] ?? null;
}
